import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { GetCaseLineItemDetail, processAction } from 'src/services/WorkflowService'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { RecordsReviewedRequest as RecordsReviewedRequest } from 'src/viewModels/workflow/RecordsReviewedRequest'

interface IRecordsReviewedRequestModalProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function RecordsReviewedRequestModal(props: IRecordsReviewedRequestModalProps) {
  const [lineItemOptions, setLineItemOptions] = useState<Array<DropdownOption>>([])
  const [selectedLineItemId, setSelectedLineItemId] = useState<string>('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {}, [props.isOpen])

  useEffect(() => {
    const fetchData = async () => {
      GetCaseLineItemDetail(+(props.associatedItemId ?? 0), false)
        .then((response) => {
          setLineItemOptions(response)

          if (response.length === 1) {
            setSelectedLineItemId(response[0].id)
          }
        })
        .catch((error) => {
          console.error('Error fetching line items:', error)
          setLineItemOptions([])
        })
    }

    fetchData()
  }, [])

  const handlesOnClose = (_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return
    }
    props.closeModal()
  }

  const validationSchema = Yup.object({
    caseLineItemId: Yup.string().required('Line item is required'),
    recordsReviewedDate: Yup.date()
      .min(new Date(2000, 0, 1), 'Date cannot be before January 1, 2000')
      .max(new Date(), 'Date cannot be in the future')
      .test('is-valid-date', 'Date must be a valid date', () => {
        const dateInput = document.getElementById(
          'recordsReviewedDate'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'Records-reviewed-date-required-with-number',
        'Date Records Reviewed is required',
        function (value) {
          return value !== null && value !== undefined
        }
      ),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  const formInitialValues = {
    caseLineItemId: selectedLineItemId,
    recordsReviewedDate: moment().format('YYYY-MM-DD'),
    comments: '',
    requireComment: props.action.requireComment,
  }

  const isInitialValid = validationSchema.isValidSync(formInitialValues)

  async function processRecordsReviewedRequest(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    const recordsRequest: RecordsReviewedRequest = {
      caseLineItemId: +values.caseLineItemId,
      recordsReviewedDate: new Date(
        moment(values.recordsReviewedDate, 'YYYY-MM-DD').toISOString()
      ),
    }

    data.recordsReviewedRequest = recordsRequest

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        if (props.handleProcessActionFinish) {
          props.handleProcessActionFinish(props.action)
        }
        setLoading(false)
        props.closeModal()
      })
      .catch((error) => {
        console.error('Error processing dictated record:', error)
        setLoading(false)
        props.closeModal()
      })
  }

  return (
    <Dialog fullWidth={true} maxWidth="md" open={props.isOpen} onClose={handlesOnClose}>
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={formInitialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          isInitialValid={isInitialValid}
          onSubmit={processRecordsReviewedRequest}
        >
          {({ values, handleChange, errors, touched, isValid, handleBlur }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!(touched.caseLineItemId && errors.caseLineItemId)}
                    required
                  >
                    <InputLabel
                      shrink={true}
                      variant="outlined"
                      style={{ color: errors.caseLineItemId ? 'red' : undefined }}
                    >
                      Line Item
                    </InputLabel>
                    <Select
                      onBlur={handleBlur}
                      variant="outlined"
                      input={<OutlinedInput notched label="Line Item" />}
                      fullWidth
                      id="caseLineItemId"
                      native={true}
                      name="caseLineItemId"
                      value={values.caseLineItemId}
                      onChange={handleChange}
                      error={!!(touched.caseLineItemId && errors.caseLineItemId)}
                      disabled={lineItemOptions.length === 1}
                    >
                      <option value="">Select a line item</option>
                      {lineItemOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.description}
                        </option>
                      ))}
                    </Select>
                    {touched.caseLineItemId && errors.caseLineItemId && (
                      <FormHelperText>{String(errors.caseLineItemId)}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item direction="row" container xs={6}>
                  <TextField
                    onBlur={handleBlur}
                    inputProps={{ id: 'recordsReviewedDate' }}
                    InputLabelProps={{ shrink: true }}
                    type="date"
                    name="recordsReviewedDate"
                    variant="outlined"
                    value={values.recordsReviewedDate}
                    onChange={handleChange}
                    label="Date Records Reviewed"
                    fullWidth
                    required
                    error={!!(touched.recordsReviewedDate && errors.recordsReviewedDate)}
                    helperText={
                      touched.recordsReviewedDate &&
                      String(errors.recordsReviewedDate || '')
                    }
                  />
                </Grid>

                {props.action.displayComment && (
                  <Grid item xs={12}>
                    <TextField
                      onBlur={handleBlur}
                      inputProps={{ id: 'comments' }}
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      name="comments"
                      variant="outlined"
                      value={values.comments}
                      onChange={handleChange}
                      label="Comments"
                      fullWidth
                      multiline
                      minRows={4}
                      required={props.action.requireComment}
                      error={!!(touched.comments && errors.comments)}
                      helperText={touched.comments && String(errors.comments || '')}
                    />
                  </Grid>
                )}
              </Grid>

              <DialogActions>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isValid}
                >
                  Execute
                </Button>
                <Button
                  onClick={() => handlesOnClose({}, 'escapeKeyDown')}
                  color="primary"
                >
                  Close
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default RecordsReviewedRequestModal
